import { bindDialog, BindDialogReturn, DialogProps, DialogResult, DialogState, useDialogState } from "../../../hooks/use-dialog-state/UseDialogState";
import { MenuItem } from "@mui/material";
import { useEffect, type JSX } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { eLynxObjectTypeEnum } from "../../../common/enums/eLynxObjectType";
import { getObjectTypes } from "../../../common/utilities/GetObjectTypeName";
import FormInput from "../../../components/forms/form-input/FormInput";
import FormPropertySection from "../../../components/forms/form-property-section/FormPropertySection";
import FormProperty from "../../../components/forms/form-property/FormProperty";
import FormSelect from "../../../components/forms/form-select/FormSelect";
import Form from "../../../components/forms/form/Form";
import ResponsiveDialog from "../../../components/responsive-dialog/ResponsiveDialog";
import UnsavedChangesPrompt from "../../../components/unsaved-changes-prompt/UnsavedChangesPrompt";
import { useConfirmation } from "../../../contexts/confirmation-context/ConfirmationContext";
import { IAuditTrailFilter } from "../../../services/audit-reporting-data-service/AuditReportingDataService";
import { AuditActionEnum } from "../../../services/audit-reporting-data-service/Models";
import { defaultAuditReportingState } from "../AuditReportingState";
import AuditFilterUtils, { AuditFilterFormValuesType } from "./AuditFilterUtils";

export type AuditFilterDialogInitValues = {
} & IAuditTrailFilter;

export type AuditFilterDialogResult = DialogResult<IAuditTrailFilter>;
export type AuditFilterDialogProps = DialogProps<AuditFilterDialogInitValues, AuditFilterDialogResult>;

export default function AuditFilterDialog({
    open,
    onClose,
    values,
}: AuditFilterDialogProps): JSX.Element {
    
    const { t } = useTranslation();
    const { confirm } = useConfirmation();

    const form = useForm<AuditFilterFormValuesType>({
        mode: "onChange",
        defaultValues: {...values}
    });

    const {
        //control,
        setValue,
        //getValues,
        handleSubmit,
        formState: {
            isDirty,
            isValid
        },
        reset,
        //watch,
        //clearErrors,
        //trigger
    } = form;


    // const [
    //     action,
    //     userLogin,
    //     objectType,
    //         ] = watch([
    //     "action", 
    //     "userLogin",
    //     "objectType",

    // ]);

    function onOkay(data: AuditFilterFormValuesType) {

        const model = AuditFilterUtils.mapToModel(data);

        onClose && onClose({
            reason: "ok",
            values: model
        });

        reset();
    }

    async function onCancel() {

        const canCancel = isDirty
            ? await getUserConfirmation()
            : true;

        if (canCancel) {
            onClose && onClose({ reason: "cancel" });
        }
    }

    async function getUserConfirmation() {

        return (await confirm({
            dialogTitle: t("cancelConfirmation.title"),
            confirmationText: t("cancelConfirmation.confirmationText"),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        })) === "yes";
    }

    function clearFields() {


        setValue('startDate', values?.startDate, {shouldDirty: true});
        setValue('endDate', values?.endDate, {shouldDirty: true});
        setValue('objectType', defaultAuditReportingState.objectType ?? eLynxObjectTypeEnum.None, {shouldDirty: true});
        setValue('objectName', defaultAuditReportingState.objectName, {shouldDirty: true});
        setValue('action', defaultAuditReportingState.auditAction ?? AuditActionEnum.None, {shouldDirty: true});
        setValue('userLogin',  defaultAuditReportingState.userName, {shouldDirty: true});
        setValue('oldValue', defaultAuditReportingState.oldValue, {shouldDirty: true});
        setValue('newValue', defaultAuditReportingState.newValue, {shouldDirty: true});
        setValue('additionalInfo',  defaultAuditReportingState.additionalInfo, {shouldDirty: true});
    }

    useEffect(() => {
        if (open) {
            reset({...values});
        }
    }, [
        open,
        values,
        reset
    ]);


    const objectTypes = getObjectTypes();

    return (
        <>

        {open ? 
            <>
            <ResponsiveDialog
                //title={t('createTag.title', {objectName: values?.objectName ?? 'New Device'})}
                title={t('auditReporting.advancedSearch')}
                enableOkay={isDirty && isValid}
                okayLabel={t("buttons.okay")}
                open={open}
                onClose={
                    async (reason: string) => {
                        reason === "ok"
                            ? handleSubmit(async (data) => onOkay(data))()
                            : onCancel();
                    }
                }
                enableAdditionalAction={true}
                additionalActionLabel={t("buttons.reset")}
                onAdditionalAction={() => clearFields()}
            >

                <Form form={form}>

                    <FormPropertySection label=''>


                    <FormProperty>
                            <FormSelect
                                name="action"
                                label={t('auditReporting.action')}
                                sx={{
                                    width: 1
                                }}
                                >
                                {/* <MenuItem disabled={true} value=''>
                                </MenuItem> */}
                                <MenuItem value='0'>
                                    {t('auditReporting.auditAction.none')}
                                </MenuItem>
                                <MenuItem value='1'>
                                    {t('auditReporting.auditAction.insert')}
                                </MenuItem>

                                <MenuItem value='2'>
                                    {t('auditReporting.auditAction.update')}
                                </MenuItem>

                                <MenuItem value='3'>
                                    {t('auditReporting.auditAction.delete')}
                                </MenuItem>

                            </FormSelect>
                        </FormProperty>


                        <FormProperty>
                            <FormInput
                                name="userLogin"
                                label={t('auditReporting.user')}
                                sx={{
                                    width: 1
                                }}
                                rules={{
                                    maxLength: 256,
                                    message: t('auditReporting.maxUserName')
                                }}
                            />
                        </FormProperty>



                        <FormProperty>
                            <FormSelect
                                name="objectType"
                                label={t('objectType')}
                                sx={{
                                    width: 1
                                }}
                                >
                                    {objectTypes.map(o =>
                                        <MenuItem key={o.Key} value={o.Key}>
                                            {o.Value}
                                        </MenuItem>
                                    )}
                            </FormSelect>
                        </FormProperty>

                        <FormProperty>
                            <FormInput
                                name="objectName"
                                label={t('auditReporting.objectName')}
                                sx={{
                                    width: 1
                                }}
                                rules={{
                                    maxLength: 64,
                                    message: t('auditReporting.maxObjectName')
                                }}
                            />
                        </FormProperty>
                        <FormProperty>
                            <FormInput
                                name="oldValue"
                                label={t('auditReporting.oldValue')}
                                sx={{
                                    width: 1
                                }}
                                rules={{
                                    maxLength: 1024,
                                    message: t('auditReporting.maxValueText')
                                }}
                            />
                        </FormProperty>
                        <FormProperty>
                            <FormInput
                                name="newValue"
                                label={t('auditReporting.newValue')}
                                sx={{
                                    width: 1
                                }}
                                rules={{
                                    maxLength: 1024,
                                    message: t('auditReporting.maxValueText')
                                }}
                            />
                        </FormProperty>
                                        
                        <FormProperty>
                            <FormInput
                                name="additionalInfo"
                                label={t('auditReporting.additionalInfo')}
                                sx={{
                                    width: 1
                                }}
                                multiline={true}
                                rows={3}
                                rules={{
                                    maxLength: 1024,
                                    message: t('auditReporting.maxValueText')
                                }}
                            />
                        </FormProperty>


                    </FormPropertySection>
                </Form>
            </ResponsiveDialog>

            <UnsavedChangesPrompt
                hasChanges={isDirty}
                getUserConfirmation={getUserConfirmation}
            />


            </>
            : null 
            }

        </>
    );
}

export type AuditFilterDialogState = DialogState<AuditFilterDialogInitValues, AuditFilterDialogResult>;
export type BindAuditFilterDialogReturn = BindDialogReturn<AuditFilterDialogInitValues, AuditFilterDialogResult>;

export function useAuditFilterDialogState(): AuditFilterDialogState {
    return useDialogState();
}

export function bindAuditFilterDialog(props: AuditFilterDialogState): BindAuditFilterDialogReturn {
    return bindDialog(props);
}
